import React, { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import _findIndex from 'lodash/findIndex';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import { useClickOutside } from '../../../hooks/useClickOutside';
import Message from '../common/Message';
import {
  DropdownFieldContainer,
  InputSection,
  Input,
  StyledCaret,
  DropdownSection,
} from './DropdownV3.styles';
import { MessageContainer, Asterisk, FormLabel, ListBoxOption } from '../form.styles';
import { debounce, scrollParentToChild, uuidv4 } from './helpers';
import DropdownListBox from '../common/DropdownListBox';

type Option = {
  label: string;
  value: string | number | null;
  alternateDisplayLabel?: React.ReactElement;
};

type ModifiedOption = Option & {
  placeholder?: boolean;
};

export type DropdownV3Props = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onSelect' | 'value'
> & {
  /** Any external effect you want to happen when the user selects an item */
  onSelect?: (s: string | number | null) => void;
  /** The items in the dropdown */
  options: Option[];
  /** The form label that appears above the select box */
  label?: string;
  /** The text that goes in the input box by default */
  placeholder?: string;
  /** Make the whole dropdown an inline element, instead of a block */
  inline?: boolean;
  /** The text to be displayed in the error message. If it doesn't evaluate to false, the component will be in an error state. */
  errorMessage?: string;
  /** A standard class string for the dropdown's outermost div */
  className?: string;
  /** Place the label to the side of the dropdown instead of over it; previously inlineLabel */
  alignment?: 'horizontal' | 'vertical';
  /** Allow user to select a null-value option with the text from the placeholder */
  nullable?: boolean;
  /**
   * Alternative text (different from the placeholder) that goes in the options list
   * when the dropdown is nullable.  If not provided, will default to the placeholder.
   */
  nullOptionLabel?: string;
  isRequired?: boolean;
  value?: string | number;
};

/**
 * `import DropdownV3 from '@zola/zola-ui/src/components/Form/dropdownV3/DropdownV3'`
 *
 * To be used standalone. Primary field is an `<input />` field to allow for browser autocomplete.
 *
 * This listbox follows the WAI-ARIA standards listed in https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html for listboxes.
 */
const DropdownV3 = ({
  onSelect,
  options,
  label,
  placeholder = 'Please select',
  inline,
  errorMessage,
  className,
  alignment = 'vertical',
  nullable,
  nullOptionLabel,
  isRequired,
  id,
  disabled,
  value,
  ...rest
}: DropdownV3Props): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [prevValue, setPrevValue] = useState<string | number | null>(null);
  const [search, setSearch] = useState('');
  // Closes dropdown if click outside
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setExpanded(false);
      if (selectedOption != null && onSelect) {
        onSelect(selectedOption.value);
      }
    },
    isOpen: expanded,
  });

  function handleOptionSelection(option: Option | null, shouldExpand: boolean) {
    setSelectedOption(option);
    if (onSelect) {
      onSelect(option?.value || null);
    }
    setExpanded(shouldExpand); // toggles the dropdown open and closed
  }

  // Set up debounce on clearing internal search
  const debouncedClear = useCallback(
    debounce(() => setSearch(''), 250),
    []
  );
  useEffect(() => {
    if (!search) return;
    const newSelection = _find(options, (o) => _includes(o.label.toLowerCase(), search));
    if (newSelection) {
      setSelectedOption(newSelection);
    }
  }, [options, search]);

  const [modifiedOptions, setModifiedOptions] = useState<ModifiedOption[]>([]);

  // Adds the placeholder to the expanded dropdown options if dropdown can be null
  useEffect(() => {
    const labelText = nullOptionLabel || placeholder;
    setModifiedOptions(
      nullable ? [{ label: labelText, value: null, placeholder: true }, ...options] : options
    );
  }, [nullable, options, placeholder, nullOptionLabel]);

  // if value is forcibly changed, select the option with new value
  useEffect(() => {
    if (value && value !== prevValue) {
      const foundOption = modifiedOptions.filter((opt) => opt.value === value)[0];
      setSelectedOption(foundOption);
      if (foundOption) {
        setPrevValue(value);
      }
    }
    // if form/input is reset eg. had previous value, now has none
    else if (prevValue && value === '') {
      setSelectedOption(null);
    }
  }, [modifiedOptions, prevValue, selectedOption, value]);

  // This is used to give each element in the dropdown a unique Id to facilite
  // scrolling to the element when the dropdown is expanded.  There may be
  // other ways to do this.
  // This shouldn't be needed, id is a required prop, but if its not provided
  // we'll still generate an id for all the dropdown options so they can be
  // scrolled
  const [idPrefix, setIdPrefix] = useState(uuidv4());

  useEffect(() => {
    if (id) {
      setIdPrefix(id);
    }
  }, [id]);

  // if an option is selected and it's not in full view of the user while the dropdown is expanded, scroll to the newly selected option
  useEffect(() => {
    if (!selectedOption) return;
    const currentIndex = _findIndex(options, (x) => x.value === selectedOption.value);
    if (
      (currentIndex || currentIndex === 0) &&
      expanded &&
      document.getElementById(`${idPrefix}-${currentIndex}`)
    ) {
      const parent =
        ref?.current &&
        (ref.current.getElementsByClassName('dropdown-v2__options')[0] as HTMLElement);
      const child = document.getElementById(`${idPrefix}-${currentIndex}`);
      if (parent && child) {
        scrollParentToChild(parent, child);
      }
    }
  }, [selectedOption, expanded, setSelectedOption, options, idPrefix]);

  // handle all key presses required by WAI-ARIA standards
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // helper function for finding the index of the current option.
    const findCurrentIndex = () => {
      return _findIndex(options, (x) => {
        if (!selectedOption) {
          return false;
        }
        return x.value === selectedOption.value;
      });
    };
    // If ESC, close the dropdown and move focus to next element
    if (e.key === 'Escape') {
      e.preventDefault();
      setExpanded(false);
      const form = (e.target as HTMLInputElement)?.form;
      if (form) {
        const index = Array.prototype.indexOf.call(form, e.target);
        (form.elements[index + 1] as HTMLElement).focus();
        e.preventDefault();
      }
    }
    if (e.key === 'Tab') {
      const currentIndex = findCurrentIndex();
      // DON'T prevent default here, we want
      // the tab key to move the user to the next
      // form element!
      setExpanded(false);
      setSelectedOption(options[currentIndex]);
      handleOptionSelection(selectedOption, false);
    }
    // If HOME, open dropdown and move to first element
    if (e.key === 'Home') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(options[0]);
    }
    // If END, open dropdown and move to last element
    if (e.key === 'End') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(options[options.length - 1]);
    }
    // If UP:
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // if no option has been selected, select last option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(options[options.length - 1]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }
      // Otherwise, move one option up. If at the top of the list, circle back to the bottom
      const currentIndex = findCurrentIndex();
      if (currentIndex === 0) {
        setSelectedOption(options[options.length - 1]);
        return;
      }
      setSelectedOption(options[Math.max(currentIndex - 1, 0)]);
      return;
    }
    // If DOWN
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // If no selected option, select the first option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(options[0]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }

      // Otherwise, move one option down. If at the bottom of the list, circle back to the top
      const currentIndex = findCurrentIndex();
      if (currentIndex === options.length - 1) {
        setSelectedOption(options[0]);
        return;
      }
      setSelectedOption(options[Math.min(currentIndex + 1, options.length - 1)]);
      return;
    }
    // I need a better test here probably, but this seemed like a good first pass
    // If the name of the key is only one letter long, treat it as search input
    if (e.key.length === 1) {
      e.preventDefault();
      setSearch(search + e.key);
      debouncedClear(); // clear the search string if it's been longer than 0.25sec
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // enter key toggles the dropdown open and closed
      handleOptionSelection(selectedOption, !expanded);
    }
  };

  return (
    <DropdownFieldContainer ref={ref} inline={inline} alignment={alignment} className={className}>
      {label && (
        <FormLabel htmlFor={id}>
          {label}
          {isRequired && <Asterisk>*</Asterisk>}
        </FormLabel>
      )}
      <DropdownSection>
        <InputSection onClick={() => !disabled && setExpanded(!expanded)}>
          <Input
            error={Boolean(errorMessage)}
            disabled={disabled}
            id={id}
            onKeyDown={handleKeyDown}
            value={selectedOption ? selectedOption.label : placeholder}
            isPlaceholder={!selectedOption?.value}
            {...rest}
          />
          <StyledCaret direction={expanded ? 'up' : 'down'} disabled={disabled} />
        </InputSection>
        {expanded && modifiedOptions.length > 0 && (
          <DropdownListBox onClose={() => setExpanded(false)} className="dropdown-v2__options">
            {modifiedOptions.map((opt, index) => (
              <ListBoxOption
                key={opt.value}
                selected={Boolean(selectedOption?.value === opt.value) && !opt.placeholder}
                isPlaceholder={opt.placeholder}
                id={`${idPrefix}-${index}`}
                onClick={() => {
                  handleOptionSelection(opt, false);
                  setExpanded(false);
                }}
              >
                {opt.alternateDisplayLabel || opt.label}
              </ListBoxOption>
            ))}
          </DropdownListBox>
        )}
        {errorMessage && (
          <MessageContainer>
            <Message type="error" text={errorMessage} className="error-message" />
          </MessageContainer>
        )}
      </DropdownSection>
    </DropdownFieldContainer>
  );
};

export default DropdownV3;
